import React, { useEffect, useState } from 'react';
import { TrendingUp, RefreshCw, Trophy, CircleSlash } from 'lucide-react';

/**
 * Admin tab: A/B test funnel for the like-auth-prompt copy.
 *
 * Variant A (control)  — "Sign in to like this video"
 * Variant B (variant)  — "Sign in to save this song"
 *
 * Pulls /api/admin/ab/like-prompt every time the user clicks Refresh (or on
 * mount). Renders impressions / CTA clicks / CTR per variant + a verdict
 * line. The verdict only declares a winner after ≥50 impressions per arm
 * AND ≥1.5 percentage-point delta — anything tighter is called inconclusive
 * server-side so the UI doesn't have to second-guess noise.
 */
export const LikePromptAbTab = ({ axiosInstance }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/ab/like-prompt');
      setStats(res.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AB stats fetch failed', err);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6" data-testid="ab-like-prompt-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            Like Prompt — A/B Test
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Which sign-in copy converts better when an anonymous user taps the heart?
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"
          data-testid="ab-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Top-level numbers */}
      <div className="grid grid-cols-2 gap-4">
        <SummaryCard label="Total impressions" value={stats?.total_impressions ?? 0} />
        <SummaryCard label="Total CTA clicks"  value={stats?.total_cta_clicks ?? 0} />
      </div>

      {/* Variant cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VariantCard
          label="Variant A (Control)"
          data={stats?.variants?.a}
          isWinner={stats?.verdict === 'a'}
          accent="cyan"
        />
        <VariantCard
          label="Variant B (Variant)"
          data={stats?.variants?.b}
          isWinner={stats?.verdict === 'b'}
          accent="orange"
        />
      </div>

      {/* Verdict banner */}
      <VerdictBanner verdict={stats?.verdict} stats={stats} />

      <p className="text-xs text-zinc-500">
        Assignment is sticky-per-session via localStorage. Each anon user is
        bucketed once and stays in the same bucket across every surface (Home
        rail, Pulse, video page, mini-player, fullscreen). CTA-click is logged
        only on the primary &quot;Sign in&quot; button, not the secondary
        &quot;Create an account&quot; link. To re-bucket yourself for a manual
        QA sanity check, clear <code className="text-zinc-300">audivue_like_ab_variant</code> from
        localStorage and tap a heart while logged out.
      </p>
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
    <p className="text-xs text-zinc-500 uppercase tracking-[0.18em]">{label}</p>
    <p className="text-2xl font-bold text-white mt-1">{Number(value).toLocaleString()}</p>
  </div>
);

const VariantCard = ({ label, data, isWinner, accent }) => {
  const ctr = data?.ctr_pct ?? 0;
  const ringColor = accent === 'orange' ? 'ring-orange-500/40' : 'ring-cyan-500/40';
  const valueColor = accent === 'orange' ? 'text-orange-400' : 'text-cyan-400';
  return (
    <div
      className={`bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 ${isWinner ? `ring-2 ${ringColor}` : ''}`}
      data-testid={`variant-card-${accent}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-300">{label}</p>
        {isWinner && (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-300">
            <Trophy className="w-3.5 h-3.5" />
            Winner
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-500 mt-2 italic">&ldquo;{data?.copy ?? '—'}&rdquo;</p>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <Mini label="Shown"     value={data?.shown ?? 0} />
        <Mini label="CTA"       value={data?.cta_click ?? 0} />
        <Mini label="CTR"       value={`${ctr}%`} accentClass={valueColor} />
      </div>
    </div>
  );
};

const Mini = ({ label, value, accentClass }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</p>
    <p className={`text-lg font-bold mt-0.5 ${accentClass || 'text-white'}`}>{value}</p>
  </div>
);

const VerdictBanner = ({ verdict, stats }) => {
  if (!stats) return null;
  if (verdict === 'inconclusive') {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex items-start gap-3" data-testid="verdict-inconclusive">
        <CircleSlash className="w-5 h-5 text-zinc-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-zinc-200">No winner yet</p>
          <p className="text-xs text-zinc-500 mt-1">
            Need ≥50 impressions per variant AND a ≥1.5 percentage-point CTR gap
            before calling a winner. Anything tighter is noise.
          </p>
        </div>
      </div>
    );
  }
  const winnerLabel = verdict === 'a' ? 'Variant A (Control)' : 'Variant B (Variant)';
  const winnerCopy  = verdict === 'a' ? stats.variants?.a?.copy : stats.variants?.b?.copy;
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid={`verdict-${verdict}`}>
      <Trophy className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-amber-200">{winnerLabel} is winning</p>
        <p className="text-xs text-zinc-400 mt-1">
          Copy: &ldquo;{winnerCopy}&rdquo;. Consider promoting this to the default
          and starting a new test against a fresh challenger.
        </p>
      </div>
    </div>
  );
};

export default LikePromptAbTab;
